import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import type React from 'react';
import * as XLSX from 'xlsx';
import { colors } from '../theme/colors';

export interface GroupedEntry {
  date: string;
  day: string;
  task: string;
  description: string;
  hours: number;
  travelTime: number;
}

export interface EmployeeData {
  employeeId: string;
  employeeName: string;
  projectName: string;
  orderNumber: string;
  month: string;
}

export interface Totals {
  hours: number;
  travelTime: number;
}

export const exportToPDF = async (
  pdfContainerRef: React.RefObject<HTMLDivElement>,
  selectedEmployeeData: EmployeeData | null,
  setIsExporting: (value: boolean) => void
): Promise<void> => {
  if (!pdfContainerRef.current || !selectedEmployeeData) {
    return;
  }

  setIsExporting(true);
  try {
    const parentElement = pdfContainerRef.current.parentElement;
    const originalParentPadding = parentElement?.style.padding || '';
    const originalParentDisplay = parentElement?.style.display || '';
    const originalParentMargin = parentElement?.style.margin || '';

    if (parentElement) {
      parentElement.style.padding = '0';
      parentElement.style.margin = '0';
      parentElement.style.display = 'block';
    }

    await new Promise((resolve) => setTimeout(resolve, 300));

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    pdf.setProperties({
      title: 'Leistungsnachweis',
      subject: 'Performance Record',
      author: 'TLT System',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();

    const pageElements = document.querySelectorAll('[data-pdf-export="true"]');

    for (let pageIndex = 0; pageIndex < pageElements.length; pageIndex++) {
      const pageElement = pageElements[pageIndex] as HTMLElement;
      
      if (pageIndex > 0) {
        pdf.addPage();
      }

      const originalBoxShadow = pageElement.style.boxShadow;
      pageElement.style.boxShadow = 'none';

      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = await html2canvas(pageElement, {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: colors.neutral.white,
        width: pageElement.offsetWidth,
        height: pageElement.offsetHeight,
        allowTaint: false,
        foreignObjectRendering: false,
      });

      pageElement.style.boxShadow = originalBoxShadow;

      const imgData = canvas.toDataURL('image/png', 1.0);
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height / canvas.width) * pdfWidth;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'SLOW');
    }

    if (parentElement) {
      parentElement.style.padding = originalParentPadding;
      parentElement.style.margin = originalParentMargin;
      parentElement.style.display = originalParentDisplay;
    }

    const sanitizedEmployeeName = selectedEmployeeData.employeeName.replace(/[^a-zA-Z0-9_-]/g, '_');
    const sanitizedMonth = selectedEmployeeData.month.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `Leistungsnachweis_${sanitizedEmployeeName}_${sanitizedMonth}.pdf`;
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Fehler beim Erstellen der PDF-Datei. Bitte versuchen Sie es erneut.');
  } finally {
    setIsExporting(false);
  }
};

export const exportToExcel = (
  selectedEmployeeData: EmployeeData | null,
  groupedEntries: GroupedEntry[],
  totals: Totals,
  setIsExporting: (value: boolean) => void
): void => {
  if (!selectedEmployeeData || groupedEntries.length === 0) {
    return;
  }

  setIsExporting(true);
  try {
    const workbook = XLSX.utils.book_new();

    const worksheetData = [
      ['Leistungsnachweis'],
      [],
      ['Name:', selectedEmployeeData.employeeName],
      ['Projektname:', selectedEmployeeData.projectName],
      ['Bestellnummer:', selectedEmployeeData.orderNumber || '-'],
      ['Monat:', selectedEmployeeData.month],
      [],
      ['Datum', 'Tag', 'Task', 'Beschreibung', 'Stunden (Std.)', 'Reisezeit (Std.)'],
    ];

    groupedEntries.forEach((entry, index) => {
      const showDate = index === 0 || groupedEntries[index - 1].date !== entry.date;
      worksheetData.push([
        showDate ? entry.date : '',
        showDate ? entry.day : '',
        entry.task,
        entry.description,
        entry.hours.toFixed(2),
        showDate ? entry.travelTime.toFixed(2) : '0.00',
      ]);
    });

    worksheetData.push([]);
    worksheetData.push(['', '', '', 'Total', totals.hours.toFixed(2), totals.travelTime.toFixed(2)]);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 20 },
      { wch: 40 },
      { wch: 12 },
      { wch: 12 },
    ];

    const totalRowIndex = worksheetData.length - 1;
    for (let col = 0; col < 6; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
      if (!worksheet[cellAddress]) {
        worksheet[cellAddress] = { t: 's', v: '' };
      }
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'F0F0F0' } },
      };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leistungsnachweis');

    const sanitizedEmployeeName = selectedEmployeeData.employeeName.replace(/[^a-zA-Z0-9_-]/g, '_');
    const sanitizedMonth = selectedEmployeeData.month.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `Leistungsnachweis_${sanitizedEmployeeName}_${sanitizedMonth}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    console.error('Error generating Excel file:', error);
    alert('Fehler beim Erstellen der Excel-Datei. Bitte versuchen Sie es erneut.');
  } finally {
    setIsExporting(false);
  }
};

